import { Card, Spin, Tooltip } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { useStationObservations } from '../../contexts/useStationObservations';
import { useTopBarStation } from '../../contexts/useTopBarStation';

// WBGT Heat Stress Risk Levels and Colors
const getWBGTColor = (wbgt: number | null): string => {
  if (wbgt === null) return '#808080'; // Gray for no data
  if (wbgt < 25) return '#52c41a'; // Green - Low risk
  if (wbgt < 28) return '#faad14'; // Yellow - Moderate risk
  if (wbgt < 31) return '#fa8c16'; // Orange - High risk
  return '#f5222d'; // Red - Very high risk
};

const getWBGTPercentage = (wbgt: number | null): number => {
  if (wbgt === null) return 0;
  // Map WBGT values to percentage (0-40°C scale)
  const minTemp = 0;
  const maxTemp = 40;
  return Math.min(Math.max(((wbgt - minTemp) / (maxTemp - minTemp)) * 100, 0), 100);
};

interface WBGTProps {
  compact?: boolean;
}

export const WBGT: React.FC<WBGTProps> = ({ compact = false }) => {
  const { theme } = useTheme();
  const { currentWeather, loading, tioLoading, dataSource } = useStationObservations();
  const { topBarStationId } = useTopBarStation();

  const isLoading = dataSource === 'stations' ? loading : tioLoading;
  const wbgt = currentWeather.wbgt;

  const getDisplayWBGT = () => {
    if (isLoading) return '...';
    if (wbgt === null) return '--';
    return wbgt.toFixed(1);
  };

  const wbgtColor = getWBGTColor(wbgt);
  const wbgtPercentage = getWBGTPercentage(wbgt);

  const card = (
    <Card
      size="small"
      style={{
        background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #d9d9d9',
        minWidth: compact ? '60px' : '90px',
        height: compact ? '32px' : '48px',
      }}
      styles={{ body: { padding: compact ? '2px 6px' : '4px 10px', height: '100%' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px', height: '100%' }}>
        <div
          style={{
            width: '4px',
            height: compact ? '18px' : '32px',
            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: '2px',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: `${wbgtPercentage}%`,
              background: wbgtColor,
              transition: 'height 0.3s ease, background 0.3s ease',
              borderRadius: '2px',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          {!compact && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: '9px',
                fontWeight: 500,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                lineHeight: '1.2',
                marginBottom: '2px',
              }}
            >
              <span>WBGT</span>
              {dataSource === 'stations' && (
                <span style={{ opacity: 0.8 }}>{topBarStationId === 'avg' ? '*' : topBarStationId}</span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <div
              style={{
                fontSize: compact ? '12px' : '16px',
                fontWeight: 600,
                color: theme === 'dark' ? 'white' : 'black',
                lineHeight: '1',
              }}
            >
              {getDisplayWBGT()}°C
            </div>
            {isLoading && <Spin size="small" />}
          </div>
        </div>
      </div>
    </Card>
  );

  return compact ? <Tooltip title="WBGT">{card}</Tooltip> : card;
};
